import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import WizardShell from '../ui/wizard-shell';
import { WizardCard, WizardKVGrid, WizardKVItem, WizardSection, WizardYesNoBadge } from '../ui/wizard-layout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { useStandort } from '../layouts/use-standort';
import CommunicationComposer, { type GroupDraftState, type RecipientGroup } from '../communications/CommunicationComposer';
import { historyTemplatesApi } from '../../lib/api';
import { communicationsApi } from '../../lib/api/communications';
import { courseOccurrenceApi } from '../../lib/courses';
import { unitIdOf } from '../../lib/unterrichtseinheiten';
import { scheduleConflictsApi, type ConflictCheckResult } from '../../lib/scheduleConflicts';

const DAYS_OF_WEEK = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

export interface CourseCancelSeed {
  id: string;
  courseId?: number | null;
  name: string;
  studentName: string;
  studentEmail: string;
  teacherId: string;
  /** Raum des Termins — für die Konfliktprüfung des Ersatztermins (T-0183). */
  roomId?: string | number | null;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  participants?: Array<{ subprofileId: string; wpUserId: number; subIndex: number; name: string }> | null;
  /** Vorgewählter Termin (YYYY-MM-DD), z. B. aus einer gemeldeten Abwesenheit (T-0190). */
  cancelDate?: string;
  /** Vorgewählt, wer absagt — bei einer Abwesenheit der Lehrkraft die Schule. */
  cancelledBy?: 'schule' | 'schueler';
}

export interface CourseCancelWizardModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  course: CourseCancelSeed;
  onDone?: () => void;
}

type Step = 1 | 2 | 3;

/** Returns the next occurrence of a given weekday (1=Mon … 7=Sun). */
function getNextCourseDate(dayOfWeek: number): Date {
  const today = new Date();
  // Convert JS getDay() (0=Sun..6=Sat) to 1=Mon..7=Sun
  const todayDow = ((today.getDay() + 6) % 7) + 1;
  const dayDiff = dayOfWeek - todayDow;
  const target = new Date(today);
  target.setDate(today.getDate() + (dayDiff >= 0 ? dayDiff : dayDiff + 7));
  return target;
}

/** Returns all future dates for a weekday within the next 3 months. */
function getFutureDatesForWeekday(dayOfWeek: number): Date[] {
  const dates: Date[] = [];
  const threeMonths = new Date();
  threeMonths.setMonth(threeMonths.getMonth() + 3);
  const cur = getNextCourseDate(dayOfWeek);
  while (cur <= threeMonths) {
    dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 7);
  }
  return dates;
}

const CourseCancelWizardModal: React.FC<CourseCancelWizardModalProps> = ({ open, onOpenChange, course, onDone }) => {
  const { selectedStandortId } = useStandort();

  const [step, setStep] = useState<Step>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step 1 — Termin
  const [cancelDate, setCancelDate] = useState('');
  const [needsAlternative, setNeedsAlternative] = useState(false);
  // Wer sagt ab? Pflicht: davon hängt beim Beitrag eine Gutschrift ab (T-0099).
  const [cancelledBy, setCancelledBy] = useState<'schule' | 'schueler' | null>(null);
  const [altStartTime, setAltStartTime] = useState('');
  const [altEndTime, setAltEndTime] = useState('');
  const [altDate, setAltDate] = useState('');
  const [historyText, setHistoryText] = useState('');
  const [historyTaskPreview, setHistoryTaskPreview] = useState<{ description?: string; dueAt?: string } | null>(null);

  // Step 2 — Channels
  const [channelEmailStudent, setChannelEmailStudent] = useState(true);
  const [channelEmailTeacher, setChannelEmailTeacher] = useState(true);
  const [communicationDrafts, setCommunicationDrafts] = useState<Record<string, GroupDraftState>>({});

  const standortId = useMemo(() => {
    const n = typeof selectedStandortId === 'string' ? Number.parseInt(selectedStandortId, 10) : (selectedStandortId ?? 0);
    return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0;
  }, [selectedStandortId]);

  // Future dates for the cancel-date picker
  const futureDates = useMemo(() => getFutureDatesForWeekday(course.dayOfWeek), [course.dayOfWeek]);

  // Reset on open / clear on close
  useEffect(() => {
    if (open) {
      setStep(1);
      setIsSubmitting(false);
      const nextDate = course.cancelDate
        ? new Date(`${course.cancelDate}T00:00:00`)
        : getNextCourseDate(course.dayOfWeek);
      setCancelDate(format(nextDate, 'yyyy-MM-dd'));
      setNeedsAlternative(false);
      setCancelledBy(course.cancelledBy ?? null);
      setAltStartTime(course.startTime);
      setAltEndTime(course.endTime);
      const suggestedAlternative = new Date(nextDate);
      suggestedAlternative.setDate(suggestedAlternative.getDate() + 7);
      setAltDate(format(suggestedAlternative, 'yyyy-MM-dd'));
      setHistoryText('');
      setHistoryTaskPreview(null);
      setChannelEmailStudent(true);
      setChannelEmailTeacher(true);
      setCommunicationDrafts({});
    } else {
      setStep(1);
      setIsSubmitting(false);
      setCancelDate('');
      setNeedsAlternative(false);
      setHistoryText('');
      setHistoryTaskPreview(null);
    }
  }, [open, course]);

  // Computed labels
  const cancelDateFormatted = useMemo(() => {
    if (!cancelDate) return '–';
    try { return format(new Date(cancelDate), 'd. MMMM yyyy', { locale: de }); } catch { return cancelDate; }
  }, [cancelDate]);

  const altDateFormatted = useMemo(() => {
    if (!altDate) return '–';
    try { return format(new Date(altDate), 'dd.MM.yyyy', { locale: de }); } catch { return altDate; }
  }, [altDate]);
  const altDayOfWeek = useMemo(() => {
    if (!altDate) return course.dayOfWeek;
    const parsed = new Date(`${altDate}T12:00:00`);
    return ((parsed.getDay() + 6) % 7) + 1;
  }, [altDate, course.dayOfWeek]);

  // History text (auto-generated if empty)
  const effectiveHistoryText = useMemo(() => {
    if (historyText.trim()) return historyText;
    let text = `Kurs "${course.name}" von ${course.studentName} wurde für den ${cancelDateFormatted} abgesagt.`;
    if (needsAlternative) {
      text += `\nAlternativtermin: ${DAYS_OF_WEEK[altDayOfWeek - 1]}, ${altStartTime} - ${altEndTime} Uhr am ${altDateFormatted}`;
    }
    return text;
  }, [historyText, course.name, course.studentName, cancelDateFormatted, needsAlternative, altDayOfWeek, altStartTime, altEndTime, altDateFormatted]);

  useEffect(() => {
    let cancelled = false;
    const loadTemplate = async () => {
      if (!open || historyText.trim()) return;
      try {
        const alternativeInfoLine = needsAlternative
          ? ` Alternativtermin: ${DAYS_OF_WEEK[altDayOfWeek - 1]}, ${altStartTime} - ${altEndTime} Uhr am ${altDateFormatted}`
          : '';
        const rendered = await historyTemplatesApi.render({
          template_key: 'course.cancelled.history',
          standort_id: standortId || undefined,
          vars: {
            courseName: course.name || '',
            cancelDate: cancelDateFormatted,
            alternativeInfoLine,
          },
        });
        if (cancelled) return;
        if (rendered.history) setHistoryText(rendered.history);
        if (rendered.task) setHistoryTaskPreview({ description: rendered.task.description, dueAt: rendered.task.dueAt });
      } catch {
        // fallback to computed history text
      }
    };
    void loadTemplate();
    return () => {
      cancelled = true;
    };
  }, [open, historyText, course.name, cancelDateFormatted, needsAlternative, altDayOfWeek, altStartTime, altEndTime, altDateFormatted, standortId]);

  // CommunicationComposer groups
  const composerGroups: RecipientGroup[] = useMemo(() => {
    const groups: RecipientGroup[] = [];
    const sharedVars: Record<string, string> = {
      studentName: course.studentName,
      courseName: course.name,
      cancelDate: cancelDateFormatted,
      dayOfWeek: DAYS_OF_WEEK[course.dayOfWeek - 1] || '',
      startTime: course.startTime,
      endTime: course.endTime,
      alternativeInfo: needsAlternative
        ? `Alternativtermin: ${DAYS_OF_WEEK[altDayOfWeek - 1]}, ${altStartTime} – ${altEndTime} Uhr am ${altDateFormatted}`
        : '',
    };

    // Student / participant group
    const studentRecipients = (course.participants || []).map(p => ({
      id: `person:${p.wpUserId}:${p.subIndex}`,
      type: 'person' as const,
      wpUserId: p.wpUserId,
      subIndex: p.subIndex,
      name: p.name,
    }));
    if (studentRecipients.length > 0) {
      groups.push({
        key: 'student',
        label: 'Teilnehmer',
        recipients: studentRecipients,
        templateKeys: { email: 'course.cancelled.student' },
        vars: sharedVars,
        defaultSelectedChannels: { email: true, sms: false },
      });
    }

    // Teacher group
    const tid = Number(course.teacherId);
    if (tid > 0) {
      groups.push({
        key: 'teacher',
        label: 'Lehrer',
        recipients: [{
          id: `person:${tid}:`,
          type: 'person' as const,
          wpUserId: tid,
          subIndex: null,
          name: `Lehrer #${tid}`,
        }],
        templateKeys: { email: 'course.cancelled.teacher' },
        vars: sharedVars,
        defaultSelectedChannels: { email: true, sms: false },
      });
    }

    return groups;
  }, [course, cancelDateFormatted, needsAlternative, altDayOfWeek, altStartTime, altEndTime, altDateFormatted]);

  // Ersatztermin live prüfen — wie beim Verschieben; bisher meldete erst der Server
  // beim Speichern den Konflikt (T-0183).
  const [ersatzKonflikt, setErsatzKonflikt] = useState<ConflictCheckResult | null>(null);
  const [ersatzPruefung, setErsatzPruefung] = useState(false);
  useEffect(() => {
    setErsatzKonflikt(null);
    if (!open || !needsAlternative || !altDate || !altStartTime || !altEndTime) return;
    const teacherId = Number(course.teacherId || 0);
    if (!(teacherId > 0)) return;
    let abgebrochen = false;
    setErsatzPruefung(true);
    scheduleConflictsApi
      .check({
        teacherId,
        roomId: course.roomId ? Number(course.roomId) : null,
        date: altDate,
        startTime: altStartTime,
        endTime: altEndTime,
        excludeUnitId: unitIdOf(course.id),
        // Der abgesagte Termin selbst belegt nichts mehr.
        excludeCourseId: course.courseId ? Number(course.courseId) : null,
        excludeDate: cancelDate || null,
      })
      .then((ergebnis) => {
        if (!abgebrochen) setErsatzKonflikt(ergebnis);
      })
      .catch(() => {
        if (!abgebrochen) setErsatzKonflikt(null);
      })
      .finally(() => {
        if (!abgebrochen) setErsatzPruefung(false);
      });
    return () => {
      abgebrochen = true;
    };
  }, [open, needsAlternative, altDate, altStartTime, altEndTime, course.teacherId, course.roomId, course.id]);

  // Validation
  const canContinueStep1 = useMemo(() => {
    if (!cancelDate || !cancelledBy) return false;
    if (needsAlternative) {
      if (!altStartTime || !altEndTime || !altDate) return false;
      if (ersatzPruefung || ersatzKonflikt?.hasBlockingConflict) return false;
    }
    return true;
  }, [cancelDate, cancelledBy, needsAlternative, altStartTime, altEndTime, altDate, ersatzPruefung, ersatzKonflikt]);

  // Navigation
  const handleNext = () => { if (step < 3) setStep((step + 1) as Step); };
  const handleBack = () => { if (step > 1) setStep((step - 1) as Step); };

  const handleConfirm = async () => {
    if (!course.courseId) {
      toast.error('Der Termin ist keinem Kurs zugeordnet.');
      return;
    }
    setIsSubmitting(true);
    try {
      await courseOccurrenceApi.cancel(course.courseId, {
        cancelDate,
        startTime: course.startTime,
        endTime: course.endTime,
        historyText: effectiveHistoryText,
        alternative: needsAlternative
          ? { date: altDate, startTime: altStartTime, endTime: altEndTime }
          : null,
        cancelledBy: cancelledBy ?? undefined,
      });

      const failedRecipients: string[] = [];
      for (const group of composerGroups) {
        const draft = communicationDrafts[group.key];
        if (!draft?.selectedChannels.email) continue;
        const response = await communicationsApi.send({
          channel: 'email',
          standortId,
          purpose: 'transactional',
          recipients: group.recipients.map((recipient) =>
            recipient.type === 'person'
              ? {
                  type: 'person' as const,
                  wpUserId: recipient.wpUserId,
                  subIndex: recipient.subIndex,
                }
              : {
                  type: 'trial_request' as const,
                  trialRequestId: recipient.trialRequestId,
                },
          ),
          templateKey: group.templateKeys?.email,
          draft: draft.email,
          vars: group.vars,
        });
        if (!response.ok) failedRecipients.push(group.label);
      }
      if (failedRecipients.length > 0) {
        toast.warning(
          `Termin wurde abgesagt, aber E-Mail-Versand fehlgeschlagen: ${failedRecipients.join(', ')}`,
        );
      } else {
        toast.success(needsAlternative ? 'Termin abgesagt und Ersatztermin angelegt' : 'Termin abgesagt');
      }
      onDone?.();
      onOpenChange(false);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Absage fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title="Termin absagen"
      description={`${course.name} – ${course.studentName}`}
      currentStep={step}
      steps={[{ label: 'Termin' }, { label: 'Benachrichtigung' }, { label: 'Übersicht' }]}
      onBack={step > 1 ? handleBack : undefined}
      canBack={step > 1 && !isSubmitting}
      onNext={step < 3 ? handleNext : undefined}
      canNext={step === 1 ? canContinueStep1 : true}
      onPrimary={step === 3 ? handleConfirm : undefined}
      primaryLabel="Absagen"
      canPrimary={!isSubmitting}
      isLoading={isSubmitting}
    >
      <WizardCard className="py-4">
        {step === 1 && (
          <>
            <WizardSection title="Termin">
              <div className="space-y-4">
                <div className="grid gap-2">
                  <Label>Aktueller Termin</Label>
                  <div className="text-sm text-muted-foreground">
                    {DAYS_OF_WEEK[course.dayOfWeek - 1]}, {course.startTime} – {course.endTime} Uhr
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label>Welcher Termin wird abgesagt?</Label>
                  <Select value={cancelDate} onValueChange={setCancelDate}>
                    <SelectTrigger><SelectValue placeholder="Datum auswählen" /></SelectTrigger>
                    <SelectContent>
                      {futureDates.map((d, i) => (
                        <SelectItem key={i} value={format(d, 'yyyy-MM-dd')}>
                          {format(d, 'dd.MM.yyyy', { locale: de })} ({DAYS_OF_WEEK[course.dayOfWeek - 1]})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Wer sagt ab?</Label>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" name="cancelledBy" checked={cancelledBy === 'schule'} onChange={() => setCancelledBy('schule')} />
                      Die Schule (z. B. Lehrkraft krank)
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" name="cancelledBy" checked={cancelledBy === 'schueler'} onChange={() => setCancelledBy('schueler')} />
                      Schüler/in bzw. Kunde
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Sagt die Schule ohne Ersatztermin ab, erhalten Verträge mit Beitrag eine anteilige Gutschrift,
                    wenn das in den Abrechnungseinstellungen so eingestellt ist.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input id="needsAlt" type="checkbox" checked={needsAlternative} onChange={(e) => setNeedsAlternative(e.target.checked)} className="h-4 w-4 rounded border-gray-300" />
                  <Label htmlFor="needsAlt">Alternativtermin festlegen</Label>
                </div>
              </div>
            </WizardSection>

            {needsAlternative && (
              <WizardSection title="Alternativtermin">
                <div className="grid gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label>Startzeit</Label>
                      <Input type="time" value={altStartTime} onChange={(e) => setAltStartTime(e.target.value)} />
                    </div>
                    <div className="grid gap-2">
                      <Label>Endzeit</Label>
                      <Input type="time" value={altEndTime} onChange={(e) => setAltEndTime(e.target.value)} />
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label>Datum</Label>
                    <Input type="date" value={altDate} onChange={(e) => setAltDate(e.target.value)} />
                  </div>
                </div>
                {ersatzPruefung ? (
                  <div className="mt-2 text-xs text-muted-foreground">Konflikte werden geprüft…</div>
                ) : ersatzKonflikt?.hasConflict ? (
                  <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-3">
                    <div className="text-sm font-medium text-red-800">Konflikt beim Ersatztermin</div>
                    <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-red-700">
                      {ersatzKonflikt.conflicts.map((c) => (
                        <li key={`${c.type}-${c.message}`}>{c.message}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </WizardSection>
            )}

            <WizardSection title="Historieneintrag (optional)">
              <Textarea
                value={historyText}
                onChange={(e) => setHistoryText(e.target.value)}
                placeholder={effectiveHistoryText}
                rows={4}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Wird in der Historie des Kurses und des Schülers gespeichert. Leer lassen für automatischen Text.
              </p>
            </WizardSection>
          </>
        )}

        {step === 2 && (
          <WizardSection title="Benachrichtigung">
            {composerGroups.length > 0 ? (
              <CommunicationComposer
                active={open && step === 2}
                standortId={standortId}
                groups={composerGroups}
                allowedChannels={{ email: true, sms: false }}
                onDraftsChange={({ groups: g }) => {
                  setCommunicationDrafts(g);
                  const s = g['student'];
                  const t = g['teacher'];
                  if (s) setChannelEmailStudent(!!s.selectedChannels.email);
                  if (t) setChannelEmailTeacher(!!t.selectedChannels.email);
                }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">Keine Empfänger verfügbar.</p>
            )}
          </WizardSection>
        )}

        {step === 3 && (
          <>
            <WizardSection title="Kursdetails">
              <WizardKVGrid>
                <WizardKVItem label="Kurs">{course.name}</WizardKVItem>
                <WizardKVItem label="Teilnehmer">{course.studentName}</WizardKVItem>
                <WizardKVItem label="Regulärer Termin">
                  {DAYS_OF_WEEK[course.dayOfWeek - 1]}, {course.startTime} – {course.endTime} Uhr
                </WizardKVItem>
                <WizardKVItem label="Abgesagter Termin">{cancelDateFormatted}</WizardKVItem>
                {needsAlternative && (
                  <WizardKVItem label="Alternativtermin">
                    {DAYS_OF_WEEK[altDayOfWeek - 1]}, {altStartTime} – {altEndTime} Uhr am {altDateFormatted}
                  </WizardKVItem>
                )}
              </WizardKVGrid>
            </WizardSection>

            <WizardSection title="Benachrichtigungen">
              <WizardKVGrid>
                <WizardKVItem label="Teilnehmer E-Mail"><WizardYesNoBadge yes={channelEmailStudent} /></WizardKVItem>
                <WizardKVItem label="Lehrer E-Mail"><WizardYesNoBadge yes={channelEmailTeacher} /></WizardKVItem>
              </WizardKVGrid>
            </WizardSection>

            <WizardSection title="Historieneintrag">
              <div className="whitespace-pre-wrap text-sm text-muted-foreground">{effectiveHistoryText}</div>
            </WizardSection>
            {historyTaskPreview ? (
              <WizardSection title="Aufgabe (aus Vorlage)">
                <div className="text-sm text-muted-foreground">
                  {historyTaskPreview.description || 'Automatische Aufgabe'}
                  {historyTaskPreview.dueAt ? ` (fällig: ${historyTaskPreview.dueAt})` : ''}
                </div>
              </WizardSection>
            ) : null}
          </>
        )}
      </WizardCard>
    </WizardShell>
  );
};

export default CourseCancelWizardModal;
